import includes from 'lodash/includes'
import get from 'lodash/get'
import forOwn from 'lodash/forOwn'
import { ApolloClient, InMemoryCache, ApolloLink, split } from '@apollo/client'
import { HttpLink } from '@apollo/client/link/http'
import { onError } from '@apollo/client/link/error'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { createUploadLink } from 'apollo-upload-client'
import { getWindow, jsonTryParse } from '@ztechapps/common.utils'
import { createClient } from 'graphql-ws'
import { getMainDefinition } from '@apollo/client/utilities'
import { routesDef } from './routes'
import { appSettings } from './app-settings'

// When the operation name is one of the whitelisted, then it should be the http link
const hasNoUserContextOperation = ({ operationName }: { operationName: string }) => {
  const whiteListOperationName = ['login', 'logout', 'fileUpload']
  return !includes(whiteListOperationName, operationName)
}

const httpErrorLink = onError(({ graphQLErrors, response, networkError }) => {
  if (graphQLErrors) {
    let errorMessage = get(graphQLErrors, '[0].message')
    errorMessage = jsonTryParse(errorMessage)
    errorMessage = get(errorMessage, '[0]')
    const exceptionCode = get(errorMessage, 'extensions.exception.code')

    // Handle the authentication error
    if (exceptionCode === 'UNAUTHORIZED') {
      return globalThis.window.location?.replace(routesDef.LoginPage.path)
    }

    let isValidationError = false
    forOwn(errorMessage, (_value, key) => {
      if (key === 'validationError') {
        isValidationError = true
      }
    })

    if (isValidationError && response) (response as any).errors = errorMessage
  } else if (networkError) {
    const statusCode = get(networkError, 'statusCode')

    // Navigate out when authentication error
    if (statusCode === 401) {
      return globalThis.window.location?.replace(routesDef.LoginPage.path)
    }
  }
})

const getWebSocketUri = () => {
  const apiUrl = import.meta.env.VITE_APP_API_URL
  const uri = new URL(apiUrl)
  const protocol = uri.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${uri.host}/subscriptions`
}

export const setupApolloClient = () => {
  const win = getWindow()
  const uri = `/graphql/?tenantId=DFT`
  const loginToken = win.localStorage.getItem(appSettings.userStorageKeys.token)

  const cache = new InMemoryCache()

  // Create the http middleware
  const authMiddleware = new ApolloLink((operation, forward) => {
    operation.setContext({
      headers: {
        'login-token': loginToken,
      },
    })
    return forward(operation)
  })

  // Create the http link
  const httpLink = new HttpLink({ uri })
  const uploadLink = createUploadLink({ uri })

  const transportLink = split(hasNoUserContextOperation, httpLink, uploadLink as any)
  const authHttpLink = ApolloLink.from([authMiddleware, httpErrorLink, transportLink])

  // Create a WebSocket link
  const wsUri = getWebSocketUri()

  const wsLink = new GraphQLWsLink(
    createClient({
      url: wsUri,
      retryAttempts: 10,
      shouldRetry: () => true,
      connectionParams: () => ({
        'login-token': loginToken,
      }),
    })
  )

  const link = split(
    // Split based on operation type
    ({ query }) => {
      const definition = getMainDefinition(query)
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
    },
    wsLink,
    authHttpLink
  )

  return new ApolloClient({
    link,
    cache,
    ssrForceFetchDelay: 100,
  })
}
